import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Input,
  Select,
} from "../../../../shared";
import { useResendCustomerInviteMutation } from "../../../../redux/services/customers/customersApi";
import { useUpdateAdminCustomerMutation } from "../../../../redux/services/admin/adminApi";
import DeleteCustomerCard from "./DeleteCustomerCard";

const PLAN_TIER_OPTIONS = [
  { value: "tier1", label: "Tier 1" },
  { value: "tier2", label: "Tier 2" },
  { value: "tier3", label: "Tier 3" },
  { value: "tier4", label: "Tier 4" },
];

const RELEASE_CHANNEL_OPTIONS = [
  { value: "stable", label: "Stable" },
  { value: "latest", label: "Latest" },
];

/** P3: office/driver/saved-report caps — monitor vs hard per tenant (API merges into billing). */
const SEAT_CAP_ENFORCEMENT_OPTIONS = [
  {
    value: "__default",
    label: "Default — follow SEAT_CAP_HARD_ENFORCE env",
  },
  { value: "monitor", label: "Monitor (log only, allow over cap)" },
  { value: "hard", label: "Hard (HTTP 402 at cap)" },
];

function buildForm(customer = {}) {
  const enforcement = customer?.billing?.enforcementMode;
  return {
    planTier: customer?.planTier || "tier1",
    releaseChannel: customer?.releaseChannel || "stable",
    betaAllowlist: Array.isArray(customer?.betaAllowlist)
      ? customer.betaAllowlist.join(", ")
      : "",
    grandfatherFullAccess: Boolean(customer?.grandfatherFullAccess),
    seatCapEnforcement:
      enforcement === "hard" || enforcement === "monitor"
        ? enforcement
        : "__default",
  };
}

function getChangeValue(input) {
  if (typeof input === "string") {
    return input;
  }

  if (input && typeof input === "object") {
    if ("value" in input) {
      return input.value;
    }
  }

  return "";
}

function parseAllowlist(value = "") {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

const SettingsTab = ({ customer, onboardingStatus }) => {
  const [form, setForm] = useState(buildForm(customer));
  const [updateCustomer, { isLoading: isSaving }] =
    useUpdateAdminCustomerMutation();
  const [resendCustomerInvite, { isLoading: isResendingInvite }] =
    useResendCustomerInviteMutation();

  useEffect(() => {
    setForm(buildForm(customer));
  }, [customer]);

  const ownerContact = useMemo(() => {
    const contacts = Array.isArray(customer?.contacts) ? customer.contacts : [];
    return (
      contacts.find((contact) => contact?.authority === "owner") || contacts[0]
    );
  }, [customer]);

  function updateFormValue(event) {
    const { id } = event;
    const value = getChangeValue(event);
    setForm((current) => ({
      ...current,
      [id]: value,
    }));
  }

  async function handleSaveSettings() {
    if (!customer?._id) {
      return;
    }

    try {
      await updateCustomer({
        id: customer._id,
        data: {
          planTier: form.planTier,
          releaseChannel: form.releaseChannel,
          betaAllowlist: parseAllowlist(form.betaAllowlist),
          grandfatherFullAccess: form.grandfatherFullAccess,
          billing: {
            enforcementMode:
              form.seatCapEnforcement === "__default"
                ? null
                : form.seatCapEnforcement,
          },
        },
      }).unwrap();

      toast("Customer settings updated", {
        type: "success",
        toastId: "customer-settings-updated",
      });
    } catch (error) {
      toast(error?.data?.message || "Unable to update customer settings", {
        type: "error",
        toastId: "customer-settings-update-error",
      });
    }
  }

  async function handleResendInvite() {
    if (!customer?._id) {
      return;
    }

    try {
      const response = await resendCustomerInvite({
        id: customer._id,
      }).unwrap();
      toast(response?.message || "Onboarding email resent successfully", {
        type: "success",
        toastId: "customer-settings-resend-invite-success",
      });
    } catch (error) {
      toast(error?.data?.message || "Unable to resend onboarding email", {
        type: "error",
        toastId: "customer-settings-resend-invite-error",
      });
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h3 className="text-lg font-medium">Onboarding Controls</h3>
            <p className="text-sm text-gray-500 mt-1">
              Manage rollout settings and help the owner finish onboarding.
            </p>
          </div>
          <Badge
            color={onboardingStatus?.color}
            label={onboardingStatus?.label}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 mt-6 md:grid-cols-2">
          <Select
            id="planTier"
            label="Plan Tier"
            value={
              PLAN_TIER_OPTIONS.find((option) => option.value === form.planTier)
                ?.label
            }
            options={PLAN_TIER_OPTIONS}
            onChange={updateFormValue}
          />
          <Select
            id="releaseChannel"
            label="Release Channel"
            value={
              RELEASE_CHANNEL_OPTIONS.find(
                (option) => option.value === form.releaseChannel,
              )?.label
            }
            options={RELEASE_CHANNEL_OPTIONS}
            onChange={updateFormValue}
          />
        </div>

        <div className="mt-4">
          <Input
            id="betaAllowlist"
            label="Beta Allowlist"
            value={form.betaAllowlist}
            onChange={updateFormValue}
            placeholder="feature-a, feature-b"
            info="Enter feature keys as a comma-separated list."
          />
        </div>

        <div className="mt-6">
          <Select
            id="seatCapEnforcement"
            label="Seat / report cap enforcement"
            value={
              SEAT_CAP_ENFORCEMENT_OPTIONS.find(
                (option) => option.value === form.seatCapEnforcement,
              )?.label
            }
            options={SEAT_CAP_ENFORCEMENT_OPTIONS}
            onChange={updateFormValue}
          />
          <p className="mt-1 text-xs text-gray-500">
            Hard blocks creates over tier limits (HTTP 402). Monitor logs only
            (tier_gate JSON). Global override on API:{" "}
            <span className="font-mono text-gray-600">
              SEAT_CAP_HARD_ENFORCE
            </span>
            .
          </p>
        </div>

        <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <Checkbox
            id="grandfatherFullAccess"
            checked={form.grandfatherFullAccess}
            label="Grandfather: full product access (ignore tier defaults)"
            onChange={(target) =>
              setForm((current) => ({
                ...current,
                grandfatherFullAccess: Boolean(target?.checked),
              }))
            }
          />
          <p className="mt-2 text-xs text-amber-900/90">
            When enabled, this tenant gets all GA features (tier packaging does
            not turn modules off), unlimited seat/report caps, and keeps new GA
            flags on by registry default. Tenant feature-flag overrides still
            apply; dev/beta lifecycle rules are unchanged.
          </p>
        </div>

        <div className="flex justify-end mt-6">
          <Button loading={isSaving} onClick={handleSaveSettings}>
            Save settings
          </Button>
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-medium">Owner Status</h3>
        <p className="text-sm text-gray-500 mt-1">
          Track the primary owner account tied to this customer.
        </p>

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <p className="text-sm font-medium text-gray-900">Owner</p>
            <p className="text-sm text-gray-500 mt-1">
              {ownerContact?.fullName || ownerContact?.email || "Not assigned"}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">Email</p>
            <p className="text-sm text-gray-500 mt-1">
              {ownerContact?.email || "No owner email available"}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">Account Status</p>
            <div className="mt-1">
              <Badge
                color={ownerContact?.active ? "green" : "yellow"}
                label={ownerContact?.active ? "Active" : "Pending"}
              />
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">Next Action</p>
            <p className="text-sm text-gray-500 mt-1">
              {onboardingStatus?.description ||
                "No onboarding status available."}
            </p>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <Button
            type="alternative"
            loading={isResendingInvite}
            disabled={!customer?._id || !ownerContact}
            onClick={handleResendInvite}
          >
            Resend invite
          </Button>
        </div>
      </Card>

      <DeleteCustomerCard customer={customer} />
    </div>
  );
};

export default SettingsTab;
